/** SPECS:
 *  __tests__/nav-group-filtering.test.ts
 *  __tests__/canonical-dashboard-routes.test.ts
 *  __tests__/crew-data-quality-nav.test.ts
 *  __tests__/hiring-jobs-page.test.ts
 *  __tests__/nav-owner-menu-5146.test.ts
 */
/**
 * Navigation Config — Role-based nav presets
 *
 * Single source of truth for what each role sees in the navigation chrome.
 * Server-compatible: no React or Lucide imports.
 * Icon values are string keys mapped to Lucide components in components/navigation/NavIconMap.tsx.
 */
import { hasPermission } from '@/lib/auth/types';
import type { Permission, Role } from '@/lib/auth/types';

// --- Types ---

export type NavItem = {
    name: string;
    href: string;
    icon: string;
    permission?: Permission;
};

export type BottomBarTab = {
    id: string;
    label: string;
    href: string;
    icon: string;
    permission?: Permission;
    activeWhen: {
        exact?: string[];
        startsWith?: string[];
    };
};

export type NavGroup = {
    group: string;
    items: NavItem[];
};

export type NavPreset = 'owner' | 'field' | 'fert';

export type NavVisibilityContext = {
    authEnabled: boolean;
    loading: boolean;
    role: Role | null;
};

/**
 * Whether a nav entry lights up for the given page address.
 *
 * An entry with a `tab=` query names a tab of its page: it lights up when the
 * page's own `tab` value matches, ignoring unrelated query values such as a
 * route date or the board-or-table choice. A plain (no-query) entry lights up
 * on its page except when a sibling entry of the same page claims the page's
 * current tab, so a tab entry and its parent entry never light up at once.
 */
export function isNavItemActive(
    item: Pick<NavItem, 'href'>,
    pathname: string,
    search: URLSearchParams,
    items: ReadonlyArray<Pick<NavItem, 'href'>>,
): boolean {
    const [hrefPath, hrefQuery] = item.href.split('?');
    const baseActive = hrefPath === '/' ? pathname === '/' : pathname.startsWith(hrefPath);
    if (!baseActive) return false;
    if (!hrefQuery) {
        const pageTab = search.get('tab');
        if (!pageTab) return true;
        return !items.some((sibling) => {
            if (sibling === item) return false;
            const [siblingPath, siblingQuery] = sibling.href.split('?');
            if (siblingPath !== hrefPath || !siblingQuery) return false;
            return new URLSearchParams(siblingQuery).get('tab') === pageTab;
        });
    }
    const entryTab = new URLSearchParams(hrefQuery).get('tab');
    if (entryTab !== null) return search.get('tab') === entryTab;
    return search.toString() === hrefQuery;
}

/**
 * Filter preset groups down to what the current user can actually open, and
 * drop groups left with no items so the sidebar never renders a heading with
 * nothing under it.
 */
export function filterNavGroups(groups: NavGroup[], ctx: NavVisibilityContext): NavGroup[] {
    const itemVisible = (item: NavItem): boolean => {
        if (ctx.loading) return false;
        if (!ctx.authEnabled) return true;
        if (!ctx.role) return false;
        return !item.permission || hasPermission(ctx.role, item.permission);
    };
    return groups
        .map((group) => ({ ...group, items: group.items.filter(itemVisible) }))
        .filter((group) => group.items.length > 0);
}

export function filterBottomBarTabs(tabs: BottomBarTab[], ctx: NavVisibilityContext): BottomBarTab[] {
    if (ctx.loading) return [];
    if (!ctx.authEnabled) return tabs;
    const role = ctx.role;
    if (!role) return [];
    return tabs.filter((tab) => !tab.permission || hasPermission(role, tab.permission));
}

// --- Preset resolution ---

export function getNavPreset(role: Role): NavPreset {
    switch (role) {
        case 'admin':
        case 'manager':
        case 'dispatcher':
            return 'owner';
        case 'fert_sub':
            return 'fert';
        case 'crew_lead':
        case 'crew':
        case 'viewer':
        default:
            return 'field';
    }
}

// --- Presets ---

export const NAV_PRESETS: Record<NavPreset, NavGroup[]> = {
    // ── Owner: plain-word entries in the order the work happens (issue #5146) ──
    // Home first; the Schedule, Customers, Estimates and Invoices entries open the
    // existing hub pages and tabs. The desktop sidebar + the More sheet content
    // come from this preset; the bottom-bar tab order/permissions live in
    // BOTTOM_BAR_TABS below. Settings lives behind the top-bar gear, and the
    // Knowledge Base and Training entries live in the top-bar user menu
    // (components/GlobalHeader.tsx); the destinations they used to open keep
    // their in-page links (Settings pricing page, Money Reports tab, Fleet page,
    // Schedule page) so every role that could open a destination today still
    // reaches it in two clicks or fewer.
    owner: [
        {
            group: 'Today',
            items: [
                { name: 'Home', href: '/', icon: 'LayoutDashboard', permission: 'view:dashboard' },
                { name: 'Schedule', href: '/ops', icon: 'Radio', permission: 'view:schedule' },
            ],
        },
        {
            group: 'Work',
            items: [
                { name: 'Customers', href: '/customers-hub', icon: 'Building2', permission: 'view:crm' },
                { name: 'Estimates', href: '/customers-hub?tab=estimates', icon: 'FileText', permission: 'view:crm' },
                { name: 'Invoices', href: '/money', icon: 'Receipt', permission: 'view:financials' },
                { name: 'Customer follow-up', href: '/comms', icon: 'MessageSquare', permission: 'view:crm' },
                { name: 'Crew Opportunities', href: '/opportunities', icon: 'Sprout', permission: 'view:crm' },
                { name: 'Seasonal Campaigns', href: '/seasonal-campaigns', icon: 'Sprout', permission: 'view:schedule' },
            ],
        },
        {
            group: 'Snow and weather',
            items: [
                { name: 'Snow Operations', href: '/snow-ops', icon: 'Snowflake', permission: 'view:schedule' },
                { name: 'Preseason Planning', href: '/snow-planning', icon: 'Route', permission: 'view:schedule' },
                { name: 'Commercial Sales', href: '/sales/snow', icon: 'Snowflake', permission: 'view:crm' },
                { name: 'Weather', href: '/weather', icon: 'CloudRain', permission: 'view:schedule' },
            ],
        },
        {
            group: 'Business',
            items: [
                { name: 'Marketing', href: '/marketing', icon: 'Megaphone', permission: 'view:reports' },
                { name: 'Growth', href: '/growth', icon: 'Zap', permission: 'view:reports' },
                { name: 'Fleet & Equipment', href: '/fleet', icon: 'Truck', permission: 'view:schedule' },
                { name: 'Hiring', href: '/hiring', icon: 'Users', permission: 'edit:jobs' },
            ],
        },
    ],

    // ── Field: minimal fallback for crew users hitting dashboard ──
    field: [
        {
            group: 'Field',
            items: [
                { name: 'Dashboard', href: '/crew', icon: 'LayoutDashboard', permission: 'view:schedule' },
                { name: 'Pricing Review', href: '/pricing-review', icon: 'DollarSign', permission: 'view:schedule' },
                { name: 'Training', href: '/training', icon: 'GraduationCap', permission: 'view:training' },
            ],
        },
    ],

    // ── Fert Sub: fertilization contractor — single page access ──
    fert: [
        {
            group: 'Fertilization',
            items: [
                { name: 'Fert Rounds', href: '/dispatch/fertilization', icon: 'Leaf', permission: 'view:schedule' },
                { name: 'Training', href: '/training', icon: 'GraduationCap', permission: 'view:training' },
            ],
        },
    ],
};

// --- Bottom bar tabs ---

export const BOTTOM_BAR_TABS: BottomBarTab[] = [
    {
        id: 'ops',
        label: 'Schedule',
        href: '/ops',
        icon: 'Radio',
        permission: 'view:schedule',
        activeWhen: {
            exact: ['/ops'],
            startsWith: ['/dispatch', '/routes', '/crews', '/time-tracking', '/requests'],
        },
    },
    {
        id: 'customers',
        label: 'Customers',
        href: '/customers-hub',
        icon: 'Building2',
        permission: 'view:crm',
        activeWhen: {
            exact: ['/customers-hub'],
            startsWith: ['/customers', '/properties', '/admin/customers', '/estimates'],
        },
    },
    {
        id: 'billing',
        label: 'Invoices',
        href: '/money',
        icon: 'Receipt',
        permission: 'view:financials',
        activeWhen: {
            exact: ['/money'],
            startsWith: ['/billing', '/financials', '/reports'],
        },
    },
    {
        id: 'respond',
        label: 'Follow-up',
        href: '/comms',
        icon: 'MessageSquare',
        permission: 'view:crm',
        activeWhen: {
            exact: ['/comms'],
            startsWith: ['/comms'],
        },
    },
    {
        id: 'opportunities',
        label: 'Grow',
        href: '/opportunities',
        icon: 'Sprout',
        permission: 'view:crm',
        activeWhen: {
            exact: ['/opportunities'],
            startsWith: ['/opportunities'],
        },
    },
];
